/**
 * useResearchSession - Hook for managing research session state on the Verdict landing page.
 *
 * Handles:
 * - Query submission (free-report API or authenticated generate)
 * - Real-time progress streaming via SSE (for authenticated users)
 * - Research phase transitions
 * - Task breakdown display
 * - Error handling and retry logic
 *
 * Flow:
 * - Free users: Task breakdown shown immediately, simulated progress (no SSE history_id available)
 * - Authenticated users: Real SSE streaming with actual progress updates
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { api } from '@/lib/api';
import { useProgressStream } from './useProgressStream';
import type { TaskBreakdown, FreeReportResponse, GenerationProgress, ResearchIntensity, TaskCapability, ResearchMode } from '@/types';
import type { DataSourceTimestamp, RejectedArgument, RejectionReason } from '@/types/researchModes';

/** Research session phases */
export type ResearchPhase =
  | 'idle' // Ready for query input
  | 'submitting' // API call in progress
  | 'planning' // Task breakdown being generated
  | 'researching' // Agents gathering information
  | 'deliberating' // Agents debating findings
  | 'concluded' // Verdict delivered
  | 'error'; // Something went wrong

/** Active agent during research */
export interface ActiveAgent {
  id: string;
  name: string;
  model: string;
  status: 'idle' | 'researching' | 'thinking' | 'speaking';
  color: string;
  /** Preview of agent's output (first 500 chars) - per task */
  outputPreview?: string;
  /** Latency in seconds for the agent's response */
  latencySeconds?: number;
  /** Full output for the agent (for modal view) */
  fullOutput?: string;
}

/** Per-task agent status tracking */
export interface TaskAgentStatus {
  taskIndex: number;
  agentIndex: number;
  status: 'idle' | 'researching' | 'thinking' | 'complete';
  latencySeconds?: number;
  outputPreview?: string;
}

/** Fact-check progress state per task */
export interface FactCheckProgress {
  taskIndex: number;
  isActive: boolean;
  isComplete: boolean;
  model?: string;
  provider?: string;
  confidenceScore?: number;
  isValidated?: boolean;
  keyFindings?: string[];
  discrepancies?: string[];
  challengePoints?: string[];
  needsDebate?: boolean;
}

/** Agent output from research task */
export interface AgentOutput {
  agentName: string;
  modelName: string;
  output: string;
  sources?: string[];
  provider?: string;
  latencySeconds?: number;
  webSourcesUsed?: number;
  success?: boolean;
}

/** Fact-check results for a task */
export interface TaskFactCheck {
  isValidated: boolean;
  keyFindings: string[];
  discrepancies: string[];
  challengePoints?: string[];
  model?: string;
  provider?: string;
}

/** Debate results for a task */
export interface TaskDebateResult {
  roundsCompleted: number;
  agreementScore: number;
  finalConsensus?: string;
  mediatorModel?: string;
  resolvedDiscrepancies: string[];
}

/** Individual task result with agent outputs */
export interface TaskResult {
  taskName: string;
  taskDescription: string;
  agentOutputs: AgentOutput[];
  aggregatedOutput: string;
  confidenceScore?: number;
  /** Estimated credit cost for this task */
  creditsUsed?: number;
  /** Whether a debate occurred for this task */
  debateOccurred?: boolean;
  /** Web sources used for this task */
  webSources?: Array<{ url: string; title: string }>;
  /** Fact-check results */
  factCheck?: TaskFactCheck;
  /** Debate results (if debate occurred) */
  debate?: TaskDebateResult;
}

/** Debate argument from an agent */
export interface DebateArgument {
  agentName: string;
  modelName: string;
  argument: string;
  roundNumber: number;
  sequenceInRound: number;
  timestamp: number;
}

/** Debate state during deliberation */
export interface DebateState {
  /** Is debate currently active? */
  isActive: boolean;
  /** Current round number */
  currentRound: number;
  /** Maximum rounds allowed */
  maxRounds: number;
  /** Agent currently speaking (if any) */
  speakingAgent: string | null;
  /** Mediator's opening statement */
  mediatorOpening: string | null;
  /** All arguments from the debate */
  arguments: DebateArgument[];
  /** Current consensus/agreement score (0-1) */
  consensusProgress: number;
  /** Discrepancies being debated */
  discrepancies: string[];
  /** Final consensus from mediator */
  finalConsensus: string | null;
  /** Task index this debate belongs to */
  taskIndex: number;
  /** Debate mode: stress_test (1-round assumption challenge), full (multi-round), or showcase */
  debateMode: 'stress_test' | 'full' | 'showcase' | null;
  /** Specific claims/assumptions that were challenged during debate */
  challengesTested: string[];
  /** Challenges that the conclusion survived */
  challengesHeld: string[];
  /** Conditions under which this conclusion should be revisited */
  watchItems: string[];
}

/** Source with URL and title */
export interface SourceInfo {
  url: string;
  title: string;
}

/** Activity feed item for streaming progress display */
export interface ActivityFeedItem {
  id: string;
  timestamp: number;
  type: 'info' | 'agent' | 'source' | 'fact-check' | 'debate' | 'warning' | 'success';
  message: string;
  /** Optional agent name for agent-specific messages */
  agentName?: string;
  /** Optional model name */
  modelName?: string;
  /** Optional task index */
  taskIndex?: number;
  /** Optional source count */
  sourceCount?: number;
  /** Optional confidence score */
  confidenceScore?: number;
}

/** Financial Kill Sheet data from FinancialOrchestrator */
export interface FinancialKillSheetData {
  ticker: string;
  analysis_date: string;
  winning_side: 'bull' | 'bear';
  confidence: number;
  bull_thesis: string;
  bear_thesis: string;
  kill_sheet_reason?: string;
  decisive_factor?: string;  // Deprecated, use kill_sheet_reason
  rejected_arguments: Array<{
    side: string;
    agent_role: string;
    agent_model: string;
    argument_summary: string;
    rejection_reason: string;
    rejection_detail: string;
    counter_evidence?: string;
    sources_cited: string[];
  }>;
  caveats: string;
  data_sources: Record<string, string>;
  research_depth: string;
  total_agents: number;
  debate_rounds: number;
  stance?: string;
  bull_intensity?: number;
  bear_risk?: number;
  catalysts?: string[];
  risks?: string[];
  watch_items?: Array<{
    trigger: string;
    impact: string;
    timeframe: string;
    importance: string;
  }>;
}

/** Anchored hard data snapshot from financial analysis
 * Field names match HardDataSnapshot.to_dict() from backend (camelCase)
 */
export interface AnchoredSnapshot {
  ticker: string;
  companyName?: string;
  asOf?: string;             // timestamp field from backend
  status: string;
  price?: number;
  priceFormatted?: string;
  currency?: string;
  changePercent?: number;    // camelCase to match backend
  changePercentFormatted?: string;
  priceMovement?: string;
  marketCap?: number;        // camelCase to match backend
  marketCapFormatted?: string;
  peTTM?: number;            // camelCase to match backend
  peFormatted?: string;
  epsTTM?: number;           // camelCase to match backend
  epsFormatted?: string;
  peForward?: number;
  forwardEPS?: number;
  volume?: number;
  avgVolume?: number;
  dayHigh?: number;
  dayLow?: number;
  yearHigh?: number;         // camelCase to match backend
  yearLow?: number;          // camelCase to match backend
  fiftyTwoWeekPosition?: number;
  sourceUrls?: string[];
  provider?: string;
  errorMessage?: string;
}

/** Final report data for display */
export interface ReportData {
  /** The final aggregated verdict/conclusion */
  verdict: string;
  /** Individual task results with agent outputs */
  taskResults: TaskResult[];
  /** Overall confidence score */
  confidenceScore?: number;
  /** S3 URL for full HTML report */
  s3Url?: string;
  /** Full HTML content (for authenticated users) */
  htmlContent?: string;
  /** Sources used across all tasks (with titles) */
  sources: SourceInfo[];
  /** Total generation time */
  durationSeconds?: number;
  /** Data source timestamps for radical transparency */
  dataTimestamps?: DataSourceTimestamp[];
  /** Rejected arguments from debate (Kill Sheet) */
  rejectedArguments?: RejectedArgument[];
  /** Financial Kill Sheet data (for mode='financial') */
  financialKillSheet?: FinancialKillSheetData;
  /** Anchored hard data snapshot (for mode='financial') */
  anchoredSnapshot?: AnchoredSnapshot;
  /** Total estimated credits used across all tasks */
  totalCreditsUsed?: number;
}

/** Agent output data stored per task-agent combination */
export interface AgentOutputData {
  taskIndex: number;
  agentIndex: number;
  agentName: string;
  model: string;
  provider: string;
  outputPreview: string;
  fullOutput?: string;
  latencySeconds: number;
  success: boolean;
  webSourcesUsed: number;
  /** Estimated credit cost for this agent call */
  creditsUsed?: number;
}

/** Research session state */
export interface ResearchSession {
  query: string;
  phase: ResearchPhase;
  tasks: TaskBreakdown[];
  currentTaskIndex: number;
  totalTasks: number;
  agents: ActiveAgent[];
  reportId: string | null;
  historyId: string | null; // For SSE streaming (authenticated users)
  email: string | null;
  reportsRemaining: number | null;
  planningTimeMs: number | null;
  error: string | null;
  isNewLead: boolean;
  /** Final report data when research concludes */
  reportData: ReportData | null;
  /** Whether the verdict is sealed (2nd+ query, needs email to unseal) */
  isSealed: boolean;
  /** Headline for sealed verdict */
  sealedHeadline: string | null;
  /** Debate state during deliberation (global, for modal display) */
  debate: DebateState | null;
  /** Completed debates per task, keyed by task index */
  debatesPerTask: Map<number, DebateState>;
  /** Agent outputs keyed by "taskIndex-agentIndex" */
  agentOutputs: Map<string, AgentOutputData>;
  /** Per-task agent status tracking keyed by "taskIndex-agentIndex" */
  taskAgentStatuses: Map<string, TaskAgentStatus>;
  /** Fact-check progress per task keyed by taskIndex */
  factCheckProgress: Map<number, FactCheckProgress>;
  /** Whether final aggregation is currently in progress */
  isFinalAggregating: boolean;
  // Smart routing fields
  /** Requested research intensity */
  requestedIntensity: ResearchIntensity | null;
  /** Effective intensity (may be adjusted by tier) */
  effectiveIntensity: ResearchIntensity | null;
  /** Credit multiplier display string */
  creditMultiplier: string | null;
  /** Activity feed for streaming progress messages */
  activityFeed: ActivityFeedItem[];
  /** Total sources found across all tasks */
  totalSourcesFound: number;
  /** Research start time for duration tracking */
  researchStartTime: number | null;
  /** Total estimated credits used across all tasks */
  totalCreditsUsed: number;
  // Research mode/blueprint fields
  /** Requested research mode (auto, financial, strategic) */
  requestedMode: ResearchMode | null;
  /** Effective mode (may be auto-detected or user-selected) */
  effectiveMode: ResearchMode | null;
}

/** Initial session state */
const initialSession: ResearchSession = {
  query: '',
  phase: 'idle',
  tasks: [],
  currentTaskIndex: 0,
  totalTasks: 0,
  agents: [],
  reportId: null,
  historyId: null,
  email: null,
  reportsRemaining: null,
  planningTimeMs: null,
  error: null,
  isNewLead: false,
  reportData: null,
  isSealed: false,
  sealedHeadline: null,
  debate: null,
  debatesPerTask: new Map(),
  agentOutputs: new Map(),
  taskAgentStatuses: new Map(),
  factCheckProgress: new Map(),
  isFinalAggregating: false,
  // Smart routing initial state
  requestedIntensity: null,
  effectiveIntensity: null,
  creditMultiplier: null,
  // Activity feed initial state
  activityFeed: [],
  totalSourcesFound: 0,
  researchStartTime: null,
  // Credit tracking
  totalCreditsUsed: 0,
  // Research mode initial state
  requestedMode: null,
  effectiveMode: null,
};

/** Agent pool - various models that can be used for research */
const agentPool: ActiveAgent[] = [
  { id: 'openai', name: 'GPT-4o Mini', model: 'openai/gpt-4o-mini', status: 'idle', color: '#10B981' },
  { id: 'anthropic', name: 'Claude 3.5 Haiku', model: 'anthropic/claude-3-5-haiku', status: 'idle', color: '#F97316' },
  { id: 'gemini', name: 'Gemini 2.5 Flash', model: 'gemini/gemini-2.5-flash', status: 'idle', color: '#3B82F6' },
  { id: 'grok', name: 'Grok Fast', model: 'xai/grok-4-fast', status: 'idle', color: '#8B5CF6' },
  { id: 'kimi', name: 'Kimi K2.5', model: 'openai/kimi-k2.5', status: 'idle', color: '#EC4899' },
];

/** Agent count per intensity level (matches backend INTENSITY_LIMITS) */
const AGENT_COUNT_BY_INTENSITY: Record<ResearchIntensity, number> = {
  fast: 2,
  deep: 3,
};

/** Get agents for a given intensity level */
function getAgentsForIntensity(intensity: ResearchIntensity = 'fast'): ActiveAgent[] {
  const count = AGENT_COUNT_BY_INTENSITY[intensity] || 2;
  return agentPool.slice(0, count).map(a => ({ ...a, status: 'idle' as const }));
}

// Note: Use getAgentsForIntensity() to get agents dynamically based on intensity level

/** Generate unique ID for activity feed items */
function generateActivityId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Add an activity feed item to the session.
 * This surfaces streaming events in the UI instead of just logging to console.
 */
function addActivityFeedItem(
  updateSession: (updates: Partial<ResearchSession>) => void,
  currentFeed: ActivityFeedItem[],
  item: Omit<ActivityFeedItem, 'id' | 'timestamp'>
): ActivityFeedItem[] {
  const newItem: ActivityFeedItem = {
    ...item,
    id: generateActivityId(),
    timestamp: Date.now(),
  };
  // Limit feed to last 50 items to prevent memory issues
  const newFeed = [...currentFeed, newItem].slice(-50);
  updateSession({ activityFeed: newFeed });
  return newFeed;
}

/**
 * Simulate agent activity for sealed verdict flow (A/B test Variant A).
 * Shows brief agent activity animation, then concludes with sealed state.
 * The verdict remains sealed until user provides email to unseal.
 */
function simulateSealedActivity(
  updateSession: (updates: Partial<ResearchSession>) => void,
  intervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
  totalTasks: number,
  intensity: ResearchIntensity = 'fast'
) {
  const agents = [...getAgentsForIntensity(intensity)];
  let currentAgent = 0;
  let taskIndex = 0;
  const maxTasks = Math.min(totalTasks, 3); // Shorter simulation for sealed verdict

  if (intervalRef.current) {
    clearInterval(intervalRef.current);
  }

  intervalRef.current = setInterval(() => {
    // Rotate through agents during simulation
    agents.forEach((a, i) => {
      a.status = i === currentAgent ? 'researching' : 'idle';
    });

    updateSession({
      agents: [...agents],
      currentTaskIndex: taskIndex,
    });

    currentAgent = (currentAgent + 1) % agents.length;
    taskIndex++;

    // After brief simulation, transition to deliberating then sealed concluded
    if (taskIndex >= maxTasks) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      // Deliberating phase
      agents.forEach((a) => (a.status = 'thinking'));
      updateSession({ phase: 'deliberating', agents: [...agents] });

      // Then conclude with sealed state - verdict is ready but hidden
      setTimeout(() => {
        updateSession({
          phase: 'concluded',
          isSealed: true,
          sealedHeadline: 'The Council has reached a High Conviction verdict.',
          // No reportData - it's sealed until email is provided
        });
      }, 3000);
    }
  }, 1500);
}

interface UseResearchSessionOptions {
  /** Callback when research completes successfully */
  onComplete?: (reportId: string) => void;
  /** Callback when an error occurs */
  onError?: (error: string) => void;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
}

export function useResearchSession(options: UseResearchSessionOptions = {}) {
  const { onComplete, onError, isAuthenticated = false } = options;
  const [session, setSession] = useState<ResearchSession>(initialSession);
  const abortControllerRef = useRef<AbortController | null>(null);
  const simulationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Update session state partially */
  const updateSession = useCallback((updates: Partial<ResearchSession>) => {
    setSession((prev) => ({ ...prev, ...updates }));
  }, []);

  /** Reset to idle state */
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (simulationIntervalRef.current) {
      clearInterval(simulationIntervalRef.current);
      simulationIntervalRef.current = null;
    }
    setSession(initialSession);
  }, []);

  /** Fetch report data when research completes */
  const fetchReportData = useCallback(
    async (historyId: string, sessionTasks: TaskBreakdown[]): Promise<ReportData | null> => {
      try {
        const newsletter = await api.getNewsletterDetail(historyId);

        // Convert session tasks to TaskResult format
        // Each task gets populated with placeholder agent outputs
        // (Full agent outputs would require additional API endpoints)
        const taskResults: TaskResult[] = sessionTasks.map((task) => ({
          taskName: task.name,
          taskDescription: task.description,
          agentOutputs: [], // Will be empty unless we add API endpoint for per-task outputs
          aggregatedOutput: '', // Could be parsed from HTML in future
          confidenceScore: undefined,
        }));

        const reportData: ReportData = {
          verdict: newsletter.content_summary || 'Research complete. View full report for details.',
          taskResults,
          sources: [],
          s3Url: newsletter.s3_url || undefined,
          htmlContent: newsletter.content || undefined,
          durationSeconds: newsletter.duration_seconds,
        };

        return reportData;
      } catch (err) {
        console.error('Failed to fetch report data:', err);
        return null;
      }
    },
    []
  );

  // SSE Progress streaming for authenticated users
  const { progress: sseProgress } = useProgressStream(session.historyId, {
    enabled: isAuthenticated && !!session.historyId && session.phase !== 'concluded',
    onComplete: async () => {
      // Fetch full report data before marking as concluded
      if (session.historyId) {
        const reportData = await fetchReportData(session.historyId, session.tasks);
        updateSession({ phase: 'concluded', reportData });
        onComplete?.(session.historyId);
      } else {
        updateSession({ phase: 'concluded' });
      }
    },
    onError: (error) => {
      updateSession({ phase: 'error', error });
      onError?.(error);
    },
  });

  // Update session state when SSE progress updates arrive
  useEffect(() => {
    if (!sseProgress || !isAuthenticated) return;

    const phaseFromSSE = mapSSEPhaseToResearchPhase(sseProgress);
    const tasksFromSSE = mapSSETasksToTaskBreakdown(sseProgress);
    // Use effectiveIntensity from session to determine agent count
    const agentsFromSSE = mapSSEProgressToAgents(sseProgress, session.effectiveIntensity || 'fast');

    updateSession({
      phase: phaseFromSSE,
      currentTaskIndex: sseProgress.current_task_index,
      totalTasks: sseProgress.total_tasks,
      tasks: tasksFromSSE.length > 0 ? tasksFromSSE : session.tasks,
      agents: agentsFromSSE,
    });
  }, [sseProgress, isAuthenticated, updateSession, session.tasks, session.effectiveIntensity]);

  /** Submit a research query (free tier - requires email) */
  const submitFreeQuery = useCallback(
    async (query: string, email: string, turnstileToken: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto') => {
      try {
        // Use requested intensity for initial agents
        const initialAgents = getAgentsForIntensity(intensity);
        updateSession({
          query,
          email,
          phase: 'submitting',
          error: null,
          requestedIntensity: intensity,
          requestedMode: mode,
          agents: initialAgents.map((a) => ({ ...a, status: 'idle' })),
          debatesPerTask: new Map(), // Clear debates from previous research
          debate: null,
        });

        const response: FreeReportResponse = await api.requestFreeReport({
          email,
          topic: query,
          turnstile_token: turnstileToken,
          intensity, // Pass intensity to API
          mode, // Pass mode to API
        });

        if (response.success) {
          const taskCount = response.tasks?.length || 0;
          // Free tier is always restricted to 'fast' by backend
          const effectiveIntensity: ResearchIntensity = (response.intensity as ResearchIntensity) || 'fast';
          const creditMultiplier = response.credit_multiplier || '1x';
          // Get agents based on effective intensity from backend
          const effectiveAgents = getAgentsForIntensity(effectiveIntensity);

          updateSession({
            phase: 'planning',
            reportId: response.report_id || null,
            reportsRemaining: response.reports_remaining,
            isNewLead: response.is_new_lead || false,
            tasks: response.tasks || [],
            totalTasks: taskCount,
            planningTimeMs: response.planning_time_ms || null,
            effectiveIntensity,
            creditMultiplier,
          });

          // Show task breakdown, then stream real-time research
          setTimeout(() => {
            updateSession({
              phase: 'researching',
              agents: effectiveAgents.map((a, i) => ({
                ...a,
                status: i === 0 ? 'researching' : 'idle',
              })),
            });

            // Stream real-time research using email for authorization
            // Get effective mode from response (backend may adjust)
            const effectiveMode = (response.mode || mode) as ResearchMode;
            streamFreeTierResearch(
              query,
              response.tasks || [],
              turnstileToken,
              updateSession,
              simulationIntervalRef,
              abortControllerRef,
              { email, mode: effectiveMode }
            );
          }, 2000);

          return response;
        } else {
          throw new Error(response.message || 'Failed to start research');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'An error occurred';
        updateSession({ phase: 'error', error: errorMsg });
        onError?.(errorMsg);
        throw err;
      }
    },
    [updateSession, onError]
  );

  /** Submit a research query via fingerprint (Sealed Verdict flow) */
  const submitFingerprintQuery = useCallback(
    async (query: string, fingerprintId: string, turnstileToken: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto') => {
      try {
        // Use requested intensity for initial agents
        const initialAgents = getAgentsForIntensity(intensity);
        updateSession({
          query,
          phase: 'submitting',
          error: null,
          isSealed: false,
          sealedHeadline: null,
          requestedIntensity: intensity,
          requestedMode: mode,
          agents: initialAgents.map((a) => ({ ...a, status: 'idle' })),
        });

        const response: FreeReportResponse = await api.requestFreeReport({
          topic: query,
          turnstile_token: turnstileToken,
          fingerprint_id: fingerprintId,
          intensity, // Pass intensity to API
          mode, // Pass mode to API
          // No email for 1st query; for 2nd+ query, backend returns sealed=true
        });

        if (response.success) {
          const taskCount = response.tasks?.length || 0;
          // Free tier is always restricted to 'fast' by backend
          const effectiveIntensity: ResearchIntensity = (response.intensity as ResearchIntensity) || 'fast';
          const creditMultiplier = response.credit_multiplier || '1x';
          // Get agents based on effective intensity from backend
          const effectiveAgents = getAgentsForIntensity(effectiveIntensity);

          // A/B TEST: Sealed Verdict Flow (Variant A)
          // For users in sealed_verdict variant, 2nd+ query returns is_sealed=true
          // Show plexiglass overlay requiring signup to see full verdict
          if (response.is_sealed) {
            // Sealed verdict - show animation, then display sealed overlay
            updateSession({
              phase: 'planning',
              isSealed: true,
              sealedHeadline: 'The Council has reached a High Conviction verdict.',
              tasks: response.tasks || [],
              totalTasks: taskCount,
              planningTimeMs: response.planning_time_ms || null,
              effectiveIntensity,
              creditMultiplier,
            });

            // Simulate the animation, then conclude with sealed state
            setTimeout(() => {
              updateSession({
                phase: 'researching',
                agents: effectiveAgents.map((a, i) => ({
                  ...a,
                  status: i === 0 ? 'researching' : 'idle',
                })),
              });

              // Simulate agent activity, but end in sealed state
              simulateSealedActivity(updateSession, simulationIntervalRef, taskCount, effectiveIntensity);
            }, 2000);

            return response;
          }

          // Full report - use SSE streaming for real-time research
          updateSession({
            phase: 'planning',
            reportId: response.report_id || null,
            isNewLead: response.is_new_lead || false,
            tasks: response.tasks || [],
            totalTasks: taskCount,
            planningTimeMs: response.planning_time_ms || null,
            effectiveIntensity,
            creditMultiplier,
          });

          // Short delay to show planning phase, then start SSE streaming
          setTimeout(() => {
            updateSession({
              phase: 'researching',
              agents: effectiveAgents.map((a, i) => ({
                ...a,
                status: i === 0 ? 'researching' : 'idle',
              })),
            });

            // Start SSE streaming for real LLM research
            // Get effective mode from response (backend may adjust)
            const effectiveMode = (response.mode || mode) as ResearchMode;
            streamFreeTierResearch(
              query,
              response.tasks || [],
              turnstileToken,
              updateSession,
              simulationIntervalRef,
              abortControllerRef,
              { fingerprintId, mode: effectiveMode }
            );
          }, 1000);

          return response;
        } else {
          throw new Error(response.message || 'Failed to start research');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'An error occurred';
        updateSession({ phase: 'error', error: errorMsg });
        onError?.(errorMsg);
        throw err;
      }
    },
    [updateSession, onError]
  );

  /** Unseal a sealed verdict by providing email */
  const unsealVerdict = useCallback(
    async (email: string, turnstileToken: string) => {
      try {
        const query = session.query;
        const originalMode = session.requestedMode || 'auto';
        if (!query) {
          throw new Error('No query to unseal');
        }

        updateSession({
          phase: 'submitting',
          email,
        });

        // Re-submit with email to get full report
        const response: FreeReportResponse = await api.requestFreeReport({
          topic: query,
          turnstile_token: turnstileToken,
          email,
          mode: originalMode,
        });

        if (response.success) {
          const taskCount = response.tasks?.length || 0;
          // Unseal uses quick intensity by default
          const unsealAgents = getAgentsForIntensity('fast');

          updateSession({
            phase: 'planning',
            isSealed: false,
            reportId: response.report_id || null,
            reportsRemaining: response.reports_remaining,
            isNewLead: response.is_new_lead || false,
            tasks: response.tasks || [],
            totalTasks: taskCount,
            planningTimeMs: response.planning_time_ms || null,
          });

          // Stream real-time research using email for authorization
          setTimeout(() => {
            updateSession({
              phase: 'researching',
              agents: unsealAgents.map((a, i) => ({
                ...a,
                status: i === 0 ? 'researching' : 'idle',
              })),
            });

            // Get effective mode from response (backend may adjust)
            const effectiveMode = (response.mode || originalMode) as ResearchMode;
            streamFreeTierResearch(
              query,
              response.tasks || [],
              turnstileToken,
              updateSession,
              simulationIntervalRef,
              abortControllerRef,
              { email, mode: effectiveMode }
            );
          }, 1500);

          return response;
        } else {
          throw new Error(response.message || 'Failed to unseal verdict');
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'An error occurred';
        updateSession({ phase: 'error', error: errorMsg });
        onError?.(errorMsg);
        throw err;
      }
    },
    [session.query, session.requestedMode, updateSession, onError]
  );

  /** Submit a research query (authenticated user) - uses streaming endpoint */
  const submitAuthenticatedQuery = useCallback(
    async (query: string, sessionId?: string, intensity: ResearchIntensity = 'fast', mode: ResearchMode = 'auto') => {
      try {
        // Use requested intensity for initial agents
        const initialAgents = getAgentsForIntensity(intensity);
        // Immediately reset blueprint state to empty to avoid showing stale data from previous research
        updateSession({
          query,
          phase: 'submitting',
          error: null,
          requestedIntensity: intensity,
          requestedMode: mode,
          agents: initialAgents.map((a) => ({ ...a, status: 'idle' })),
          debatesPerTask: new Map(), // Clear debates from previous research
          debate: null,
          // Reset blueprint state immediately to avoid showing stale completed blueprint
          tasks: [],
          currentTaskIndex: 0,
          totalTasks: 0,
          agentOutputs: new Map(),
          taskAgentStatuses: new Map(),
          factCheckProgress: new Map(),
          reportData: null,
          isFinalAggregating: false,
        });

        // Phase 1: Get task plan and session ID with intensity and mode
        const planResponse = await api.planStreamResearch(query, sessionId, intensity, mode);
        const taskCount = planResponse.tasks?.length || 0;

        // Capture effective intensity and credit multiplier from response
        const effectiveIntensity = (planResponse.intensity || intensity) as ResearchIntensity;
        const creditMultiplier = planResponse.credit_multiplier || '1x';
        // Get agents based on effective intensity from backend
        const effectiveAgents = getAgentsForIntensity(effectiveIntensity);

        updateSession({
          phase: 'planning',
          reportId: planResponse.session_id,
          tasks: planResponse.tasks.map(t => ({
            name: t.name,
            description: t.description,
            execution_mode: t.execution_mode as 'parallel' | 'sequential',
            required_capability: t.required_capability as TaskCapability | undefined,
          })),
          totalTasks: taskCount,
          planningTimeMs: planResponse.planning_time_ms,
          effectiveIntensity,
          creditMultiplier,
        });

        // Short delay to show planning phase, then start SSE streaming
        setTimeout(() => {
          updateSession({
            phase: 'researching',
            agents: effectiveAgents.map((a, i) => ({
              ...a,
              status: i === 0 ? 'researching' : 'idle',
            })),
          });

          // Phase 2: Start SSE streaming for authenticated research
          // Get effective mode from response (backend may adjust)
          const effectiveMode = (planResponse.mode || mode) as ResearchMode;
          streamAuthenticatedResearch(
            query,
            planResponse.tasks,
            planResponse.session_id,
            updateSession,
            simulationIntervalRef,
            abortControllerRef,
            onComplete,
            effectiveIntensity,
            effectiveMode,
          );
        }, 1000);

        return { session_id: planResponse.session_id, intensity: effectiveIntensity, credit_multiplier: creditMultiplier };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'An error occurred';
        updateSession({ phase: 'error', error: errorMsg });
        onError?.(errorMsg);
        throw err;
      }
    },
    [updateSession, onError, onComplete]
  );

  return {
    session,
    updateSession,
    reset,
    submitFreeQuery,
    submitFingerprintQuery,
    unsealVerdict,
    submitAuthenticatedQuery,
    isIdle: session.phase === 'idle',
    isActive: ['submitting', 'planning', 'researching', 'deliberating'].includes(session.phase),
    isComplete: session.phase === 'concluded',
    // TEMPORARILY DISABLED: Always false to test CouncilHUD without overlay
    isSealed: false, // session.isSealed && session.phase === 'concluded',
    hasError: session.phase === 'error',
  };
}

/**
 * Stream free-tier research in real-time via SSE.
 * Supports both fingerprint (anonymous 1st query) and email-based flows.
 * Connects to /api/v1/free-report/stream endpoint.
 */
async function streamFreeTierResearch(
  topic: string,
  tasks: TaskBreakdown[],
  turnstileToken: string,
  updateSession: (updates: Partial<ResearchSession>) => void,
  intervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  options: {
    fingerprintId?: string;
    email?: string;
    mode?: ResearchMode;
  },
) {
  // Clear any existing interval
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }

  // Create abort controller for this stream
  const abortController = new AbortController();
  abortControllerRef.current = abortController;

  // Build API URL
  // In production (Vercel), VITE_API_URL is empty and we use relative URLs
  // so that Vercel's rewrites can proxy to the backend.
  // In development, we fall back to localhost:8000.
  const apiBase = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '');
  const url = `${apiBase}/api/v1/free-report/stream`;

  // Active agents state for real-time updates
  const agents: ActiveAgent[] = [
    { id: 'openai', name: 'GPT-4o Mini', model: 'openai/gpt-4o-mini', status: 'idle', color: '#10B981' },
    { id: 'anthropic', name: 'Claude 3.5 Haiku', model: 'anthropic/claude-3-5-haiku', status: 'idle', color: '#F97316' },
    { id: 'deepseek', name: 'DeepSeek Reasoner', model: 'deepseek/deepseek-reasoner', status: 'idle', color: '#8B5CF6' },
  ];

  // Task results for building final report
  const taskResults: TaskResult[] = [];

  // Agent outputs keyed by "taskIndex-agentIndex"
  const agentOutputs = new Map<string, AgentOutputData>();

  // Per-task agent statuses keyed by "taskIndex-agentIndex"
  const taskAgentStatuses = new Map<string, TaskAgentStatus>();

  // Fact-check progress per task keyed by taskIndex
  const factCheckProgress = new Map<number, FactCheckProgress>();

  // Completed debates per task keyed by taskIndex
  const debatesPerTask = new Map<number, DebateState>();

  // Current debate state (mutable reference for event handler)
  let currentDebateState: DebateState | null = null;

  // Activity feed for streaming progress messages
  let activityFeed: ActivityFeedItem[] = [];
  let totalSourcesFound = 0;

  // Set research start time
  updateSession({ researchStartTime: Date.now() });

  // Build request body - include either fingerprint or email
  const requestBody: Record<string, unknown> = {
    topic,
    turnstile_token: turnstileToken,
    tasks: tasks.map(t => ({
      name: t.name,
      description: t.description,
      prompt: t.description, // Use description as prompt
      execution_mode: t.execution_mode || 'parallel', // Include execution mode for parallel task grouping
    })),
  };

  if (options.fingerprintId) {
    requestBody.fingerprint_id = options.fingerprintId;
  }
  if (options.email) {
    requestBody.email = options.email;
  }
  if (options.mode) {
    requestBody.mode = options.mode;
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    // Track whether a terminal event (done/error) was received before stream closes
    let receivedTerminalEvent = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          // Event type is in the next data line, skip this line
          continue;
        }

        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          try {
            const data = JSON.parse(jsonStr);
            const result = handleStreamEvent(
              data, updateSession, agents, taskResults, tasks, agentOutputs,
              taskAgentStatuses, factCheckProgress, currentDebateState, debatesPerTask,
              activityFeed, totalSourcesFound
            );
            if (result.debateState !== undefined) {
              currentDebateState = result.debateState;
            }
            activityFeed = result.activityFeed;
            totalSourcesFound = result.totalSourcesFound;
            if (result.isTerminal) {
              receivedTerminalEvent = true;
            }
          } catch {
            // Ignore parse errors for heartbeats, etc.
          }
        }
      }
    }

    // Safety net: if stream ended without a terminal event (done/error),
    // the research would be stuck forever in deliberating/researching phase.
    if (!receivedTerminalEvent) {
      console.warn('[Research] Free-tier stream ended without terminal event — setting error state');
      updateSession({
        phase: 'error',
        error: 'Research stream ended unexpectedly. Please try again.',
        debate: null,
        debatesPerTask: new Map(),
      });
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Free-tier stream aborted');
      return;
    }
    // Enhanced error logging with context
    const errorDetails = {
      message: error instanceof Error ? error.message : 'Unknown error',
      name: error instanceof Error ? error.name : 'UnknownError',
      topic: topic.substring(0, 100),
      taskCount: tasks.length,
      mode: options.mode || 'auto',
      timestamp: new Date().toISOString(),
    };
    console.error('Free-tier stream error:', errorDetails, error);

    // Build user-friendly error message
    let userMessage = 'Stream failed';
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        userMessage = 'Network error - please check your connection and try again';
      } else if (error.message.includes('timeout') || error.message.includes('Timeout')) {
        userMessage = 'Request timed out - the research is taking longer than expected';
      } else if (error.message.includes('401') || error.message.includes('403')) {
        userMessage = 'Authorization error - please refresh the page and try again';
      } else {
        userMessage = error.message;
      }
    }

    // Clear debate state on error to prevent black screen overlays from persisting
    updateSession({
      phase: 'error',
      error: userMessage,
      debate: null,
      debatesPerTask: new Map(),
    });
  }
}

/**
 * Stream authenticated research in real-time via SSE.
 * Uses the authenticated /api/v1/research/stream/execute endpoint.
 */
async function streamAuthenticatedResearch(
  topic: string,
  tasks: Array<{ name: string; description: string; execution_mode: string }>,
  sessionId: string,
  updateSession: (updates: Partial<ResearchSession>) => void,
  intervalRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  onComplete?: (reportId: string) => void,
  intensity: ResearchIntensity = 'fast',
  mode: ResearchMode = 'auto',
) {
  // Clear any existing interval
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }

  // Create abort controller for this stream
  const abortController = new AbortController();
  abortControllerRef.current = abortController;

  // Build API URL
  // In production (Vercel), VITE_API_URL is empty and we use relative URLs
  // so that Vercel's rewrites can proxy to the backend.
  // In development, we fall back to localhost:8000.
  const apiBase = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8000' : '');
  const url = `${apiBase}/api/v1/research/stream/execute`;

  // Get auth token from storage (stored as 'access_token' by the auth system)
  const token = localStorage.getItem('access_token');
  if (!token) {
    updateSession({ phase: 'error', error: 'Not authenticated' });
    return;
  }

  // Active agents state for real-time updates - use intensity to determine agent count
  const agents: ActiveAgent[] = getAgentsForIntensity(intensity);

  // Task results for building final report
  const taskResults: TaskResult[] = [];

  // Agent outputs keyed by "taskIndex-agentIndex"
  const agentOutputs = new Map<string, AgentOutputData>();

  // Per-task agent statuses keyed by "taskIndex-agentIndex"
  const taskAgentStatuses = new Map<string, TaskAgentStatus>();

  // Fact-check progress per task keyed by taskIndex
  const factCheckProgress = new Map<number, FactCheckProgress>();

  // Completed debates per task keyed by taskIndex
  const debatesPerTask = new Map<number, DebateState>();

  // Current debate state (mutable reference for event handler)
  let currentDebateState: DebateState | null = null;

  // Activity feed for streaming progress messages
  let activityFeed: ActivityFeedItem[] = [];
  let totalSourcesFound = 0;

  // Track whether a terminal event (done/error) was received before stream closes
  let receivedTerminalEvent = false;

  // Set research start time
  updateSession({ researchStartTime: Date.now() });

  // Build request body (matches StreamExecuteRequest model) with intensity and mode
  const requestBody = {
    session_id: sessionId,
    topic,
    intensity,
    mode,
    tasks: tasks.map(t => ({
      name: t.name,
      description: t.description,
      execution_mode: t.execution_mode || 'parallel',
    })),
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Stream request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body reader available');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    // Convert tasks to TaskBreakdown format for handleStreamEvent
    const taskBreakdowns: TaskBreakdown[] = tasks.map(t => ({
      name: t.name,
      description: t.description,
      execution_mode: t.execution_mode as 'parallel' | 'sequential',
    }));

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete events (SSE format: "event: type\ndata: json\n\n")
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          // Ignore event type line, we determine type from data content
          continue;
        }

        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          try {
            const data = JSON.parse(jsonStr);
            const result = handleStreamEvent(
              data, updateSession, agents, taskResults, taskBreakdowns,
              agentOutputs, taskAgentStatuses, factCheckProgress, currentDebateState,
              debatesPerTask, activityFeed, totalSourcesFound
            );
            if (result.debateState !== undefined) {
              currentDebateState = result.debateState;
            }
            activityFeed = result.activityFeed;
            totalSourcesFound = result.totalSourcesFound;
            if (result.isTerminal) {
              receivedTerminalEvent = true;
            }
          } catch {
            // Ignore parse errors for heartbeats, etc.
          }
        }
      }
    }

    // Safety net: if stream ended without a terminal event (done/error),
    // the research would be stuck forever in deliberating/researching phase.
    if (!receivedTerminalEvent) {
      console.warn('[Research] Stream ended without terminal event — setting error state');
      updateSession({
        phase: 'error',
        error: 'Research stream ended unexpectedly. Please try again.',
        debate: null,
        debatesPerTask: new Map(),
      });
      return;
    }

    // Stream complete - call onComplete callback
    if (onComplete) {
      onComplete(sessionId);
    }
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('Authenticated stream aborted');
      return;
    }
    console.error('Authenticated stream error:', error);
    // Clear debate state on error to prevent black screen overlays from persisting
    updateSession({
      phase: 'error',
      error: error instanceof Error ? error.message : 'Stream failed',
      debate: null,
      debatesPerTask: new Map(),
    });
  }
}

/** Handle streaming event result - contains both debate state and activity feed */
interface StreamEventResult {
  debateState?: DebateState | null;
  activityFeed: ActivityFeedItem[];
  totalSourcesFound: number;
  /** True when this event was a terminal event (done/error) indicating the stream should end */
  isTerminal?: boolean;
}

/** Handle individual SSE events from free-tier research stream
 * Returns the new debate state and updated activity feed
 */
function handleStreamEvent(
  data: Record<string, unknown>,
  updateSession: (updates: Partial<ResearchSession>) => void,
  agents: ActiveAgent[],
  taskResults: TaskResult[],
  tasks: TaskBreakdown[],
  agentOutputs: Map<string, AgentOutputData>,
  taskAgentStatuses: Map<string, TaskAgentStatus>,
  factCheckProgress: Map<number, FactCheckProgress>,
  currentDebateState: DebateState | null,
  debatesPerTask: Map<number, DebateState>,
  activityFeed: ActivityFeedItem[],
  totalSourcesFound: number,
): StreamEventResult {
  let feed = activityFeed;
  let sources = totalSourcesFound;

  // Detect event type from data content
  if ('stage' in data) {
    const stage = data.stage as string;
    if (stage === 'researching') {
      updateSession({ phase: 'researching', isFinalAggregating: false });
      feed = addActivityFeedItem(updateSession, feed, {
        type: 'info',
        message: 'Research phase started - agents analyzing sources...',
      });
    } else if (stage === 'aggregating') {
      updateSession({ phase: 'deliberating', isFinalAggregating: true });
      feed = addActivityFeedItem(updateSession, feed, {
        type: 'info',
        message: 'Aggregating findings - synthesizing verdict...',
      });
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle models_info event (shows all models in workflow)
  if ('models' in data && 'total_models' in data) {
    console.log(`[Research] Using ${data.total_models} models:`, data.models);
    const models = data.models as Array<{ display_name: string }>;
    const modelNames = models.map(m => m.display_name).join(', ');
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'info',
      message: `Deploying ${data.total_models} AI models: ${modelNames}`,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle web_search_start event
  if ('sources_per_agent' in data && !('sources_found' in data)) {
    const taskIndex = data.task_index as number;
    const taskName = tasks[taskIndex]?.name || `Task ${taskIndex + 1}`;
    console.log(`[Research] Web search starting for task ${data.task_index}`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'source',
      message: `Searching web for "${taskName}"...`,
      taskIndex,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle web_search_complete event
  if ('sources_found' in data && 'sources' in data) {
    const taskIndex = data.task_index as number;
    const sourcesFound = data.sources_found as number;
    sources += sourcesFound;
    console.log(`[Research] Found ${data.sources_found} web sources for task ${data.task_index}`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'source',
      message: `Found ${sourcesFound} sources for analysis`,
      taskIndex,
      sourceCount: sourcesFound,
    });
    updateSession({ totalSourcesFound: sources });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle fact_check_start event
  if ('task_index' in data && 'provider' in data && !('success' in data) && !('confidence_score' in data) && !('agent_index' in data)) {
    const taskIndex = data.task_index as number;
    const model = data.model as string;
    const provider = data.provider as string;
    console.log(`[Research] Fact-checking task ${taskIndex} with ${model}`);

    // Initialize fact-check progress for this task
    factCheckProgress.set(taskIndex, {
      taskIndex,
      isActive: true,
      isComplete: false,
      model,
      provider,
    });

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'fact-check',
      message: `Fact-checking with ${model}...`,
      modelName: model,
      taskIndex,
    });

    updateSession({
      factCheckProgress: new Map(factCheckProgress),
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle fact_check_complete event
  if ('confidence_score' in data && 'is_validated' in data) {
    const taskIndex = data.task_index as number;
    const confidenceScore = data.confidence_score as number;
    const isValidated = data.is_validated as boolean;
    const keyFindings = data.key_findings as string[] || [];
    const discrepancies = data.discrepancies as string[] || [];
    const challengePoints = data.challenge_points as string[] || [];
    const needsDebate = data.needs_debate as boolean || false;
    const model = data.model as string;
    const provider = data.provider as string;

    console.log(`[Research] Fact-check complete: ${(confidenceScore * 100).toFixed(0)}% confidence, findings: ${keyFindings.length}, discrepancies: ${discrepancies.length}, challenges: ${challengePoints.length}`);

    // Update fact-check progress with results
    const existing = factCheckProgress.get(taskIndex) || { taskIndex, isActive: false, isComplete: false };
    factCheckProgress.set(taskIndex, {
      ...existing,
      isActive: false,
      isComplete: true,
      model,
      provider: provider || existing.provider,
      confidenceScore,
      isValidated,
      keyFindings,
      discrepancies,
      challengePoints,
      needsDebate,
    });

    const confidencePercent = (confidenceScore * 100).toFixed(0);
    if (needsDebate) {
      feed = addActivityFeedItem(updateSession, feed, {
        type: 'warning',
        message: `Low confidence (${confidencePercent}%) - initiating agent debate...`,
        confidenceScore,
        taskIndex,
      });
    } else {
      feed = addActivityFeedItem(updateSession, feed, {
        type: 'fact-check',
        message: `Fact-check complete: ${confidencePercent}% confidence`,
        confidenceScore,
        taskIndex,
      });
    }

    updateSession({
      factCheckProgress: new Map(factCheckProgress),
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_start event
  if ('discrepancies' in data && 'max_rounds' in data && !('opening_statement' in data)) {
    const taskIndex = (data.task_index as number) ?? 0;
    console.log(`[Research] Debate starting for task ${taskIndex}: ${data.reason}`);
    const discrepancies = data.discrepancies as string[];
    const maxRounds = data.max_rounds as number;

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Debate initiated: ${discrepancies.length} discrepancies to resolve`,
      taskIndex,
    });

    // Parse debate mode from backend event (stress_test, full, showcase)
    const debateMode = (data.debate_mode as string | undefined) as DebateState['debateMode'] ?? null;

    // Initialize debate state with task index
    const newDebate: DebateState = {
      isActive: true,
      currentRound: 0,
      maxRounds,
      speakingAgent: null,
      mediatorOpening: null,
      arguments: [],
      consensusProgress: 0,
      discrepancies,
      finalConsensus: null,
      taskIndex,
      challengesTested: [],
      challengesHeld: [],
      watchItems: [],
      debateMode,
    };

    // Set all agents to "speaking" during debate
    agents.forEach(a => a.status = 'speaking');
    // Eagerly write to debatesPerTask so the debate stays pinned to the correct task box
    const updatedDebatesPerTask = new Map(debatesPerTask);
    updatedDebatesPerTask.set(taskIndex, newDebate);
    updateSession({ agents: [...agents], phase: 'deliberating', debate: newDebate, debatesPerTask: updatedDebatesPerTask });
    return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_mediator_open event (new)
  if ('opening_statement' in data && 'discrepancies' in data && 'mediator_model' in data) {
    const taskIndex = (data.task_index as number) ?? currentDebateState?.taskIndex ?? 0;
    const mediatorModel = data.mediator_model as string;
    console.log(`[Research] Mediator (${mediatorModel}) opening debate for task ${taskIndex}`);
    const openingStatement = data.opening_statement as string;
    const discrepancies = data.discrepancies as string[];

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Mediator (${mediatorModel}) framing the debate...`,
      agentName: 'Mediator',
      modelName: mediatorModel,
      taskIndex,
    });

    // Update debate state with mediator opening
    const baseDebate = currentDebateState || {
      isActive: true, currentRound: 0, maxRounds: 2, speakingAgent: null,
      mediatorOpening: null, arguments: [], consensusProgress: 0, discrepancies: [], finalConsensus: null, taskIndex,
      debateMode: null, challengesTested: [], challengesHeld: [], watchItems: [],
    };

    const newDebate: DebateState = {
      ...baseDebate,
      mediatorOpening: openingStatement,
      discrepancies,
      speakingAgent: 'Mediator',
      taskIndex,
    };

    // Update debatesPerTask to keep debate pinned to correct task
    const updatedDebatesPerTask = new Map(debatesPerTask);
    updatedDebatesPerTask.set(taskIndex, newDebate);
    updateSession({ debate: newDebate, debatesPerTask: updatedDebatesPerTask });
    return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_round_start event (new)
  if ('round_number' in data && 'max_rounds' in data && !('participants' in data) && !('agreement_score' in data)) {
    const roundNumber = data.round_number as number;
    const maxRounds = data.max_rounds as number;
    console.log(`[Research] Debate Round ${roundNumber} starting`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Debate Round ${roundNumber}/${maxRounds} starting...`,
    });

    if (currentDebateState) {
      const newDebate: DebateState = {
        ...currentDebateState,
        currentRound: roundNumber,
        speakingAgent: null, // Will be set when agent starts speaking
      };
      const updatedDebatesPerTask = new Map(debatesPerTask);
      updatedDebatesPerTask.set(newDebate.taskIndex, newDebate);
      updateSession({ debate: newDebate, debatesPerTask: updatedDebatesPerTask });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_agent_argument event (new)
  if ('argument' in data && 'agent_name' in data && 'round_number' in data && 'sequence_in_round' in data) {
    const agentName = data.agent_name as string;
    const modelName = data.model_name as string;
    const roundNumber = data.round_number as number;
    const sequenceInRound = data.sequence_in_round as number;
    const totalDebaters = data.total_debaters as number;

    console.log(`[Research] ${agentName} (${sequenceInRound}/${totalDebaters}) presents argument in Round ${roundNumber}`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `${agentName} presenting argument (${sequenceInRound}/${totalDebaters})`,
      agentName,
      modelName,
    });

    if (currentDebateState) {
      const newArgument: DebateArgument = {
        agentName,
        modelName,
        argument: data.argument as string,
        roundNumber,
        sequenceInRound,
        timestamp: Date.now(),
      };

      // Find and update the speaking agent's status
      const agentId = agentName.toLowerCase().includes('gpt') ? 'gpt4'
        : agentName.toLowerCase().includes('claude') ? 'claude'
        : 'gemini';

      agents.forEach(a => {
        a.status = a.id === agentId ? 'speaking' : 'thinking';
      });

      const newDebate: DebateState = {
        ...currentDebateState,
        speakingAgent: agentName,
        arguments: [...currentDebateState.arguments, newArgument],
      };

      const updatedDebatesPerTask = new Map(debatesPerTask);
      updatedDebatesPerTask.set(newDebate.taskIndex, newDebate);
      updateSession({
        agents: [...agents],
        debate: newDebate,
        debatesPerTask: updatedDebatesPerTask,
      });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_round_complete event (new)
  if ('round_number' in data && 'agreement_score' in data && 'participants' in data && !('final_consensus' in data)) {
    const roundNumber = data.round_number as number;
    const agreementScore = data.agreement_score as number;
    const participants = data.participants as string[];

    console.log(`[Research] Round ${roundNumber} complete: ${(agreementScore * 100).toFixed(0)}% agreement (${participants.length} participants)`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Round ${roundNumber} complete: ${(agreementScore * 100).toFixed(0)}% agreement`,
      confidenceScore: agreementScore,
    });

    if (currentDebateState) {
      const newDebate: DebateState = {
        ...currentDebateState,
        consensusProgress: agreementScore,
        speakingAgent: null, // Round ended
      };
      const updatedDebatesPerTask = new Map(debatesPerTask);
      updatedDebatesPerTask.set(newDebate.taskIndex, newDebate);
      updateSession({ debate: newDebate, debatesPerTask: updatedDebatesPerTask });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_mediator_synthesizing event (keeps SSE alive during mediator LLM call)
  if ('mediator_synthesizing' in data && !('final_consensus' in data)) {
    const taskIndex = (data.task_index as number) ?? currentDebateState?.taskIndex ?? 0;
    const agreementScore = data.agreement_score as number;

    console.log(`[Research] Mediator synthesizing consensus for task ${taskIndex} (${(agreementScore * 100).toFixed(0)}% agreement)...`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Mediator forming consensus (${(agreementScore * 100).toFixed(0)}% agreement)...`,
      agentName: 'Mediator',
      taskIndex,
    });

    if (currentDebateState) {
      const newDebate: DebateState = {
        ...currentDebateState,
        speakingAgent: 'Mediator',
        // Keep isActive: true so the debate UI stays visible
      };
      const updatedDebatesPerTask = new Map(debatesPerTask);
      updatedDebatesPerTask.set(newDebate.taskIndex, newDebate);
      updateSession({ debate: newDebate, debatesPerTask: updatedDebatesPerTask });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_mediator_close event (new)
  if ('final_consensus' in data && 'agreement_score' in data && 'mediator_model' in data) {
    const agreementScore = data.agreement_score as number;
    const consensusReached = data.consensus_reached as boolean;
    // Extract task_index from event data, fallback to currentDebateState
    const taskIndex = (data.task_index as number) ?? currentDebateState?.taskIndex ?? 0;

    console.log(`[Research] Mediator closes debate for task ${taskIndex}: ${(agreementScore * 100).toFixed(0)}% agreement ${consensusReached ? '(CONSENSUS)' : ''}`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: consensusReached ? 'success' : 'warning',
      message: consensusReached
        ? `Consensus reached: ${(agreementScore * 100).toFixed(0)}% agreement`
        : `Debate concluded: ${(agreementScore * 100).toFixed(0)}% agreement (no consensus)`,
      confidenceScore: agreementScore,
      taskIndex,
    });

    if (currentDebateState) {
      // Set all agents to idle after debate
      agents.forEach(a => a.status = 'idle');

      const newDebate: DebateState = {
        ...currentDebateState,
        isActive: false,
        finalConsensus: data.final_consensus as string,
        consensusProgress: agreementScore,
        speakingAgent: 'Mediator',
        taskIndex, // Ensure taskIndex is set from event data
        challengesTested: (data.challenges_tested as string[]) || currentDebateState.challengesTested || [],
        challengesHeld: (data.challenges_held as string[]) || currentDebateState.challengesHeld || [],
        watchItems: (data.watch_items as string[]) || currentDebateState.watchItems || [],
      };

      // Store completed debate in per-task map for persistent display
      const updatedDebatesPerTask = new Map(debatesPerTask);
      updatedDebatesPerTask.set(taskIndex, newDebate);

      updateSession({
        agents: [...agents],
        debate: newDebate,
        debatesPerTask: updatedDebatesPerTask,
      });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle debate_complete event (legacy, still emitted for backward compatibility)
  if ('rounds_completed' in data && 'agreement_score' in data && !('final_consensus' in data)) {
    const consensusReached = data.consensus_reached as boolean;
    const agreementScore = data.agreement_score as number;
    console.log(`[Research] Debate complete: ${(agreementScore * 100).toFixed(0)}% agreement ${consensusReached ? '(consensus)' : ''}`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Debate complete: ${(agreementScore * 100).toFixed(0)}% agreement`,
      confidenceScore: agreementScore,
    });

    // Mark debate as complete
    if (currentDebateState) {
      const newDebate: DebateState = {
        ...currentDebateState,
        isActive: false,
      };
      updateSession({ debate: newDebate });
      return { debateState: newDebate, activityFeed: feed, totalSourcesFound: sources };
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  if ('agent_index' in data && 'task_index' in data) {
    const agentIndex = data.agent_index as number;
    const taskIndex = data.task_index as number;
    const model = data.model as string;
    const provider = data.provider as string;

    // Update agent with actual model info
    if (agentIndex < agents.length) {
      agents[agentIndex].name = model;
      agents[agentIndex].model = `${provider}/${model}`;
    }

    const statusKey = `${taskIndex}-${agentIndex}`;

    // agent_start event
    if (!('success' in data)) {
      agents[agentIndex].status = 'researching';

      // Track per-task agent status
      taskAgentStatuses.set(statusKey, {
        taskIndex,
        agentIndex,
        status: 'researching',
      });

      feed = addActivityFeedItem(updateSession, feed, {
        type: 'agent',
        message: `${model} starting analysis...`,
        agentName: model,
        modelName: `${provider}/${model}`,
        taskIndex,
      });

      updateSession({
        agents: [...agents],
        currentTaskIndex: taskIndex,
        taskAgentStatuses: new Map(taskAgentStatuses),
      });
      return { activityFeed: feed, totalSourcesFound: sources };
    }

    // agent_complete event - capture output_preview and full_output
    const outputPreview = data.output_preview as string || '';
    const fullOutput = data.full_output as string || outputPreview;  // Fallback to preview if no full output
    const latencySeconds = data.latency_seconds as number || 0;
    const success = data.success as boolean;
    const webSourcesUsed = data.web_sources_used as number || 0;
    const creditCost = (data.credit_cost as number) || 0;

    feed = addActivityFeedItem(updateSession, feed, {
      type: success ? 'agent' : 'warning',
      message: success
        ? `${model} complete (${latencySeconds.toFixed(1)}s, ${webSourcesUsed} sources)`
        : `${model} failed`,
      agentName: model,
      modelName: `${provider}/${model}`,
      taskIndex,
      sourceCount: webSourcesUsed,
    });

    // Store the agent output data with full output for modal display
    agentOutputs.set(statusKey, {
      taskIndex,
      agentIndex,
      agentName: model,
      model: `${provider}/${model}`,
      provider,
      outputPreview,
      fullOutput,  // Include full output for Logic Tree modal
      latencySeconds,
      success,
      webSourcesUsed,
      creditsUsed: creditCost,
    });

    // Track per-task agent status as complete
    taskAgentStatuses.set(statusKey, {
      taskIndex,
      agentIndex,
      status: 'complete',
      latencySeconds,
      outputPreview,
    });

    // Update agent status and store output preview
    // Only update global agent status if this is the current task
    agents[agentIndex].status = success ? 'thinking' : 'idle';
    agents[agentIndex].outputPreview = outputPreview;
    agents[agentIndex].latencySeconds = latencySeconds;

    // Update session with both agent status and the agent outputs map
    updateSession({
      agents: [...agents],
      taskAgentStatuses: new Map(taskAgentStatuses),
      agentOutputs: new Map(agentOutputs),
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  if ('task_name' in data && 'aggregated_output' in data && !('final_summary' in data)) {
    // task_complete event
    const taskName = data.task_name as string;
    const aggregatedOutput = data.aggregated_output as string;
    const taskIndex = data.task_index as number;
    const confidenceScore = data.confidence_score as number | undefined;
    const debateOccurred = data.debate_occurred as boolean | undefined;
    const webSourcesUsed = data.web_sources_used as number | undefined;
    const taskCreditCost = (data.credit_cost as number) || 0;

    console.log(`[Research] Task ${taskIndex + 1} complete: ${taskName}${confidenceScore ? ` (${(confidenceScore * 100).toFixed(0)}% confidence)` : ''}${debateOccurred ? ' [debated]' : ''}${webSourcesUsed ? ` [${webSourcesUsed} sources]` : ''}`);

    const confidenceText = confidenceScore ? ` (${(confidenceScore * 100).toFixed(0)}% confidence)` : '';
    const debateText = debateOccurred ? ' [debated]' : '';
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Task complete: ${taskName}${confidenceText}${debateText}`,
      taskIndex,
      confidenceScore,
    });

    // Add to task results
    taskResults.push({
      taskName,
      taskDescription: tasks[taskIndex]?.description || '',
      agentOutputs: [], // Individual outputs not stored for simplicity
      aggregatedOutput,
      confidenceScore,
      creditsUsed: taskCreditCost,
    });

    // Accumulate total credits
    const currentTotal = taskResults.reduce((sum, r) => sum + (r.creditsUsed || 0), 0);
    updateSession({ totalCreditsUsed: currentTotal + taskCreditCost });

    // Reset agent statuses for next task
    agents.forEach(a => a.status = 'idle');
    updateSession({ agents: [...agents] });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // ==========================================================================
  // FINANCIAL DEEP DIVE EVENTS (from FinancialOrchestrator)
  // ==========================================================================

  // Handle financial deep dive start event
  if ('ticker' in data && 'depth' in data && 'total_agents' in data && !('kill_sheet' in data)) {
    const ticker = data.ticker as string;
    const depth = data.depth as string;
    const totalAgents = data.total_agents as number;
    console.log(`[Financial] Starting deep dive for ${ticker} (${depth}, ${totalAgents} agents)`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'info',
      message: `Financial Deep Dive: ${ticker} (${depth} depth, ${totalAgents} agents)`,
    });

    updateSession({ phase: 'researching' });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle hard data fetch events
  if ('message' in data && (data.message as string).includes('anchored market data')) {
    console.log('[Financial] Fetching anchored market data...');
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'info',
      message: data.message as string,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle hard data ready event
  if ('snapshot' in data && 'status' in data && !('kill_sheet' in data)) {
    const status = data.status as string;
    const message = data.message as string || 'Market data ready';
    console.log(`[Financial] Hard data ready: ${status}`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: status === 'ok' ? 'success' : 'warning',
      message,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle data fetch start (FMP/SEC)
  if ('message' in data && (data.message as string).includes('FMP and SEC')) {
    console.log('[Financial] Fetching FMP and SEC data...');
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'info',
      message: 'Fetching financial data from FMP and SEC EDGAR...',
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle data fetch complete
  if ('fmp_price' in data && 'has_10k' in data) {
    const has10k = data.has_10k as boolean;
    const has10q = data.has_10q as boolean;
    console.log(`[Financial] Data fetch complete: 10-K=${has10k}, 10-Q=${has10q}`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Financial data loaded (10-K: ${has10k ? 'Yes' : 'No'}, 10-Q: ${has10q ? 'Yes' : 'No'})`,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle Bull team start
  if ('agent_count' in data && 'message' in data && (data.message as string).includes('Bull')) {
    const agentCount = data.agent_count as number;
    console.log(`[Financial] Bull team starting (${agentCount} agents)`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'agent',
      message: `Bull Case team deploying (${agentCount} agents)`,
      agentName: 'Bull Team',
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle Bear team start
  if ('agent_count' in data && 'message' in data && (data.message as string).includes('Bear')) {
    const agentCount = data.agent_count as number;
    console.log(`[Financial] Bear team starting (${agentCount} agents)`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'agent',
      message: `Bear Case team deploying (${agentCount} agents)`,
      agentName: 'Bear Team',
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle financial agent start/complete (side-specific)
  if ('side' in data && 'agent_role' in data) {
    const side = data.side as string;
    const agentRole = data.agent_role as string;
    // Note: agent_index is available in data but not used currently

    if (!('latency' in data)) {
      // Agent start
      console.log(`[Financial] ${side.toUpperCase()} agent ${agentRole} starting...`);
      feed = addActivityFeedItem(updateSession, feed, {
        type: 'agent',
        message: `${side.charAt(0).toUpperCase() + side.slice(1)} ${agentRole} analyzing...`,
        agentName: `${side.toUpperCase()}-${agentRole}`,
      });
    } else {
      // Agent complete
      const latency = data.latency as number;
      const success = data.success as boolean;
      const citationCount = data.citation_count as number;
      console.log(`[Financial] ${side.toUpperCase()} agent ${agentRole} complete (${latency.toFixed(1)}s, ${citationCount} citations)`);
      feed = addActivityFeedItem(updateSession, feed, {
        type: success ? 'success' : 'warning',
        message: `${side.charAt(0).toUpperCase() + side.slice(1)} ${agentRole}: ${citationCount} citations (${latency.toFixed(1)}s)`,
        agentName: `${side.toUpperCase()}-${agentRole}`,
      });
    }
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle Bull/Bear team complete
  if ('completed' in data && 'total_citations' in data && !('winning_side' in data)) {
    const completed = data.completed as number;
    const totalCitations = data.total_citations as number;
    console.log(`[Financial] Team complete: ${completed} agents, ${totalCitations} total citations`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Team analysis complete (${completed} agents, ${totalCitations} citations)`,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle financial debate start
  if ('debate_rounds' in data && 'message' in data && (data.message as string).includes('adversarial')) {
    const debateRounds = data.debate_rounds as number;
    console.log(`[Financial] Adversarial debate starting (${debateRounds} rounds)`);

    updateSession({ phase: 'deliberating' });
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'debate',
      message: `Bull vs Bear debate: ${debateRounds} rounds`,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle financial debate complete
  if ('winning_side' in data && 'confidence' in data && !('kill_sheet' in data)) {
    const winningSide = data.winning_side as string;
    const confidence = data.confidence as number;
    console.log(`[Financial] Debate complete: ${winningSide} wins (${(confidence * 100).toFixed(0)}% confidence)`);
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `${winningSide.toUpperCase()} case wins (${(confidence * 100).toFixed(0)}% confidence)`,
    });
    return { activityFeed: feed, totalSourcesFound: sources };
  }

  // Handle financial done event with kill_sheet
  if ('kill_sheet' in data) {
    const killSheet = data.kill_sheet as FinancialKillSheetData;
    const summary = data.summary as string;
    const anchoredSnapshot = data.anchored_snapshot as AnchoredSnapshot | undefined;
    const financialTotalCost = (data.total_cost as number) || 0;

    // DEBUG: Log raw data to check anchored_snapshot
    console.log('[Financial] Raw done event data keys:', Object.keys(data));
    console.log('[Financial] data.anchored_snapshot:', data.anchored_snapshot);
    console.log('[Financial] anchoredSnapshot after cast:', anchoredSnapshot);
    console.log(`[Financial] Kill Sheet ready: ${killSheet.winning_side.toUpperCase()} wins (${(killSheet.confidence * 100).toFixed(0)}% confidence)`);

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Financial Deep Dive complete: ${killSheet.winning_side.toUpperCase()} verdict at ${(killSheet.confidence * 100).toFixed(0)}% confidence`,
    });

    const reportData: ReportData = {
      verdict: summary || `${killSheet.winning_side.toUpperCase()} case prevails with ${(killSheet.confidence * 100).toFixed(0)}% confidence. ${killSheet.kill_sheet_reason || killSheet.decisive_factor || ''}`,
      taskResults: [], // Financial mode doesn't use task structure
      sources: Object.entries(killSheet.data_sources).map(([name, ts]) => ({
        url: '',
        title: `${name}: ${ts}`,
      })),
      confidenceScore: killSheet.confidence,
      financialKillSheet: killSheet,
      anchoredSnapshot: anchoredSnapshot,
      totalCreditsUsed: financialTotalCost,
    };

    agents.forEach(a => a.status = 'speaking');
    updateSession({
      phase: 'concluded',
      agents: [...agents],
      reportData,
      isFinalAggregating: false,
      debate: null,
    });
    return { activityFeed: feed, totalSourcesFound: sources, isTerminal: true };
  }

  // ==========================================================================
  // STANDARD RESEARCH EVENTS (from FreeTierResearchRunner)
  // ==========================================================================

  if ('final_summary' in data) {
    // done event - research complete
    const finalSummary = data.final_summary as string;
    const totalLatency = data.total_latency_seconds as number;
    const totalWebSources = data.total_web_sources as number | undefined;
    const totalCost = (data.total_cost as number) || taskResults.reduce((sum, r) => sum + (r.creditsUsed || 0), 0) || 0;
    const modelsUsed = data.models_used as Array<{ display_name: string; provider: string; role: string }> | undefined;
    const tasksData = data.tasks as Array<{
      name: string;
      description: string;
      aggregated_output: string;
      confidence_score?: number;
      debate_occurred?: boolean;
      web_sources?: Array<{ url: string; title: string }>;
      agents: Array<{
        name: string;
        model: string;
        provider: string;
        success: boolean;
        full_output?: string;
        output_preview?: string;
        latency_seconds?: number;
        web_sources_used?: number;
      }>;
      fact_check?: {
        is_validated: boolean;
        key_findings: string[];
        discrepancies: string[];
        model?: string;
        provider?: string;
      };
      debate?: {
        rounds_completed: number;
        agreement_score: number;
        final_consensus?: string;
        mediator_model?: string;
        resolved_discrepancies: string[];
      };
    }>;

    // Log comprehensive summary
    console.log(`[Research] Done! ${totalLatency?.toFixed(1)}s, ${totalWebSources || 0} web sources, ${modelsUsed?.length || 0} models`);

    // Add final success message to activity feed
    feed = addActivityFeedItem(updateSession, feed, {
      type: 'success',
      message: `Verdict delivered! Analyzed ${totalWebSources || sources} sources in ${totalLatency?.toFixed(1)}s`,
      sourceCount: totalWebSources || sources,
    });

    // Collect all web sources with titles (deduped by URL)
    const allSources: SourceInfo[] = [];
    const seenUrls = new Set<string>();
    tasksData.forEach(t => {
      t.web_sources?.forEach(s => {
        if (!seenUrls.has(s.url)) {
          seenUrls.add(s.url);
          allSources.push({ url: s.url, title: s.title || '' });
        }
      });
    });

    // Build complete report data with full agent outputs for export
    const finalTaskResults: TaskResult[] = tasksData.map((t) => ({
      taskName: t.name,
      taskDescription: t.description,
      agentOutputs: t.agents.map((a) => ({
        agentName: a.name,
        modelName: a.model,
        output: a.full_output || a.output_preview || '',
        provider: a.provider,
        latencySeconds: a.latency_seconds,
        webSourcesUsed: a.web_sources_used,
        success: a.success,
      })),
      aggregatedOutput: t.aggregated_output,
      confidenceScore: t.confidence_score,
      debateOccurred: t.debate_occurred,
      webSources: t.web_sources,
      factCheck: t.fact_check ? {
        isValidated: t.fact_check.is_validated,
        keyFindings: t.fact_check.key_findings || [],
        discrepancies: t.fact_check.discrepancies || [],
        model: t.fact_check.model,
        provider: t.fact_check.provider,
      } : undefined,
      debate: t.debate ? {
        roundsCompleted: t.debate.rounds_completed,
        agreementScore: t.debate.agreement_score,
        finalConsensus: t.debate.final_consensus,
        mediatorModel: t.debate.mediator_model,
        resolvedDiscrepancies: t.debate.resolved_discrepancies || [],
      } : undefined,
    }));

    // Compute overall confidence score from task averages
    const tasksWithConfidence = finalTaskResults.filter(t => t.confidenceScore !== undefined);
    const overallConfidence = tasksWithConfidence.length > 0
      ? tasksWithConfidence.reduce((sum, t) => sum + (t.confidenceScore || 0), 0) / tasksWithConfidence.length
      : undefined;

    // Extract data timestamps for "Radical Transparency"
    const dataTimestamps = data.data_timestamps as Array<{
      source: string;
      dataType: string;
      timestamp: string;
      delay: string;
      isRealtime: boolean;
    }> | undefined;

    // Extract rejected arguments for Kill Sheet
    const rejectedArguments = data.rejected_arguments as Array<{
      id: string;
      argumentSummary: string;
      arguedBy: string;
      agentModel: string;
      rejectionReason: string;
      rejectionDetail: string;
      counterEvidence: string;
      sourcesCited: string[];
      taskIndex: number;
    }> | undefined;

    const reportData: ReportData = {
      verdict: finalSummary,
      taskResults: finalTaskResults,
      confidenceScore: overallConfidence,
      sources: allSources,
      durationSeconds: totalLatency,
      dataTimestamps: dataTimestamps?.map(ts => ({
        source: ts.source,
        dataType: ts.dataType,
        timestamp: ts.timestamp,
        delay: ts.delay,
        isRealtime: ts.isRealtime,
      })),
      rejectedArguments: rejectedArguments?.map(ra => ({
        id: ra.id,
        argumentSummary: ra.argumentSummary,
        arguedBy: ra.arguedBy,
        agentModel: ra.agentModel,
        rejectionReason: ra.rejectionReason as RejectionReason,
        rejectionDetail: ra.rejectionDetail,
        counterEvidence: ra.counterEvidence,
        sourcesCited: ra.sourcesCited,
        taskIndex: ra.taskIndex,
      })),
      totalCreditsUsed: totalCost,
    };

    // Mark all agents as speaking (concluded)
    agents.forEach(a => a.status = 'speaking');
    updateSession({
      phase: 'concluded',
      agents: [...agents],
      reportData,
      isFinalAggregating: false,
      totalSourcesFound: totalWebSources || sources,
      debate: null, // Clear debate state to prevent stale overlays
    });
    return { activityFeed: feed, totalSourcesFound: totalWebSources || sources, isTerminal: true };
  }

  if ('error' in data) {
    const errorMessage = data.error as string;
    console.error('[SSE Error Event]', { error: errorMessage, fullData: data });

    // Parse error message for user-friendly display
    const errorCode = (data as Record<string, unknown>).code as string | undefined;
    let userFriendlyError = errorMessage;
    if (errorCode === 'INSUFFICIENT_CREDITS') {
      const available = (data as Record<string, unknown>).available as number | undefined;
      userFriendlyError = available !== undefined
        ? `Insufficient credits (${available} remaining). Purchase more credits to continue.`
        : 'Insufficient credits. Purchase more credits to continue.';
    } else if (errorMessage.includes('Enter email to continue') || errorMessage.includes('Sign up to continue')) {
      userFriendlyError = 'You\'ve used your free queries. Sign up to continue with research!';
    } else if (errorMessage.includes('free_query_limit_reached') || errorMessage.includes('free queries')) {
      userFriendlyError = 'Sign up to continue your research and get more credits.';
    } else if (errorMessage.includes('rate limit') || errorMessage.includes('Rate limit')) {
      userFriendlyError = 'Too many requests - please wait a moment and try again';
    } else if (errorMessage.includes('API key') || errorMessage.includes('authentication')) {
      userFriendlyError = 'Service temporarily unavailable - please try again later';
    }

    feed = addActivityFeedItem(updateSession, feed, {
      type: 'warning',
      message: `Error: ${userFriendlyError}`,
    });
    updateSession({
      phase: 'error',
      error: userFriendlyError,
      debate: null,
      debatesPerTask: new Map(),
    });
    return { activityFeed: feed, totalSourcesFound: sources, isTerminal: true };
  }

  // Return default result for unhandled events
  return { activityFeed: feed, totalSourcesFound: sources };
}

/** Map SSE phase to ResearchPhase */
function mapSSEPhaseToResearchPhase(progress: GenerationProgress): ResearchPhase {
  switch (progress.status) {
    case 'queued':
      return 'planning';
    case 'generating':
      // Map current_phase to more specific phase
      if (progress.current_phase === 'final_aggregation') {
        return 'deliberating';
      }
      return 'researching';
    case 'completed':
      return 'concluded';
    case 'failed':
      return 'error';
    default:
      return 'researching';
  }
}

/** Map SSE sub_tasks to TaskBreakdown array */
function mapSSETasksToTaskBreakdown(progress: GenerationProgress): TaskBreakdown[] {
  if (!progress.sub_tasks || progress.sub_tasks.length === 0) {
    return [];
  }

  return progress.sub_tasks.map((subTask) => ({
    name: subTask.name,
    description: subTask.description,
    // Extract execution_mode from sub_task if available, default to 'parallel'
    execution_mode: ((subTask as unknown) as Record<string, unknown>).execution_mode as 'parallel' | 'sequential' || 'parallel',
  }));
}

/** Map SSE progress to agent activity states */
function mapSSEProgressToAgents(progress: GenerationProgress, intensity: ResearchIntensity = 'fast'): ActiveAgent[] {
  const agents = [...getAgentsForIntensity(intensity)];

  // Check current sub_task for agent activity
  const currentSubTask = progress.sub_tasks?.[progress.current_task_index];

  if (currentSubTask && currentSubTask.status === 'in_progress') {
    // During active research, show agents working
    const activeSteps = currentSubTask.steps?.filter((s) => s.status === 'in_progress') || [];

    agents.forEach((agent, i) => {
      // Map step activity to agent status based on step content
      const agentStep = activeSteps.find(
        (s) =>
          s.name.toLowerCase().includes(agent.name.toLowerCase()) ||
          s.details?.toLowerCase().includes(agent.model.toLowerCase())
      );

      if (agentStep) {
        agent.status = 'researching';
      } else if (i === progress.current_task_index % agents.length) {
        // Fallback: rotate through agents based on current task
        agent.status = 'researching';
      } else if (i === (progress.current_task_index + 1) % agents.length) {
        agent.status = 'thinking';
      } else {
        agent.status = 'idle';
      }
    });
  } else if (progress.current_phase === 'final_aggregation') {
    // During deliberation, show all agents thinking/speaking
    agents.forEach((agent, i) => {
      agent.status = i % 2 === 0 ? 'thinking' : 'speaking';
    });
  }

  return agents;
}

